import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useBankFromQueryParam, useBrandFromQueryParam } from './useBankFromQueryParam';
import { isLocalStorageAvailable, storage } from '../../utils/storage';
import { BankType, BrandType } from '../../generated/graphql';
import { config } from '../../config';
import { session } from '../../utils/auth';

export type BankContextTypes = BankType | undefined | string;
type SetSelectedType<T> = (value?: T) => void;

export const BankContext = createContext({
  bankSelected: undefined as (BankContextTypes),
  setBankSelected: (() => null) as SetSelectedType<BankContextTypes>,
});

export const useBankContext = () => {
  const { bankSelected, setBankSelected: setContextBankSelected } = useContext(BankContext);

  const setBankSelected = useCallback(
    (bank: BankContextTypes) => {
      setContextBankSelected(bank);
    }, [ setContextBankSelected ],
  );

  return [ bankSelected, setBankSelected ] as const;
};

const BrandContext = createContext({
  brandSelected: undefined as (BrandType | undefined),
  setBrandSelected: (() => null) as SetSelectedType<BrandType | undefined>,
});

export const useBrandContext = () => {
  const { brandSelected, setBrandSelected: setContextBrandSelected } = useContext(BrandContext);

  const setBrandSelected = useCallback(
    (brand: BrandType | undefined) => {
      setContextBrandSelected(brand);
    }, [ setContextBrandSelected ],
  );

  return [ brandSelected, setBrandSelected ] as const;
};

const BrandContextProvider: React.FC = ({ children }) => {
  const queryBrand = useBrandFromQueryParam();
  const [ brand, setBrand ] = useState<BrandType | undefined>();

  const brandSelected = useMemo(() => {
    if (brand) return brand;
    const storageBrand = isLocalStorageAvailable() ? storage.getItem(config.brandKey) : undefined;
    if (storageBrand) return storageBrand as BrandType | undefined;
    return session.brand;
  }, [ brand ]);

  const setBrandSelected = useCallback((brandValue: BrandType | undefined) => {
    if (brandValue && isLocalStorageAvailable()) storage.setItem(config.brandKey, brandValue);
    setBrand(brandValue);
  }, [ setBrand ]);

  useEffect(() => {
    if (!queryBrand) return;
    setBrandSelected(queryBrand);
  }, [ setBrandSelected, queryBrand ]);

  return (
    <BrandContext.Provider value={{ brandSelected, setBrandSelected }}>
      {children}
    </BrandContext.Provider>
  );
};

export const BankContextProvider: React.FC = ({ children }) => {
  const [ bank, setBank ] = useState<BankContextTypes>();
  const queryBank = useBankFromQueryParam();

  const bankSelected = useMemo(() => {
    if (bank) return bank;
    const storageBank = isLocalStorageAvailable() ? storage.getItem(config.bankKey) : undefined;
    if (storageBank) return storageBank;
    return session.bank;
  }, [ bank ]);

  const setBankSelected = useCallback((bankValue: BankContextTypes) => {
    if (bankValue && isLocalStorageAvailable()) storage.setItem(config.bankKey, bankValue);
    setBank(bankValue);
  }, [ setBank ]);

  useEffect(() => {
    if (!queryBank) return;
    setBankSelected(queryBank);
  }, [ setBankSelected, queryBank ]);

  return (
    <BrandContextProvider>
      <BankContext.Provider value={{ bankSelected, setBankSelected }}>
        {children}
      </BankContext.Provider>
    </BrandContextProvider>
  );
};
